/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {Trans, useLingui} from '@lingui/react/macro';
import {CheckIcon, XIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';
import * as AuthenticationActionCreators from '~/actions/AuthenticationActionCreators';
import {VerificationResult} from '~/actions/AuthenticationActionCreators';
import {AuthRouterLink} from '~/components/auth/AuthRouterLink';
import {Spinner} from '~/components/uikit/Spinner';
import {useFluxerDocumentTitle} from '~/hooks/useFluxerDocumentTitle';
import {useHashParam} from '~/hooks/useHashParam';
import {createVerificationError, type VerificationError, VerificationErrorType} from '~/types/VerificationError';
import styles from './AuthorizeIPPage.module.css';

const renderErrorMessage = (error: VerificationError | null) => {
	if (!error) return null;

	switch (error.type) {
		case VerificationErrorType.LINK_EXPIRED:
			return <Trans>Link expired. Your IP address was likely already authorized.</Trans>;
		default:
			return <Trans>An unexpected server error occurred. Please try reloading the page or logging in again.</Trans>;
	}
};

const AuthorizeIPPage = observer(function AuthorizeIPPage() {
	const {t} = useLingui();
	const [isLoading, setIsLoading] = useState(true);
	const [isSuccess, setIsSuccess] = useState(false);
	const [error, setError] = useState<VerificationError | null>(null);

	useFluxerDocumentTitle(t`Authorize IP`);

	const token = useHashParam('token');

	useEffect(() => {
		const performAuthorization = async () => {
			if (!token) {
				setError(createVerificationError(VerificationErrorType.INVALID_TOKEN));
				setIsLoading(false);
				return;
			}

			const result = await AuthenticationActionCreators.authorizeIp(token);

			switch (result) {
				case VerificationResult.SUCCESS:
					setIsSuccess(true);
					break;
				case VerificationResult.EXPIRED_TOKEN:
					setError(createVerificationError(VerificationErrorType.LINK_EXPIRED));
					break;
				case VerificationResult.SERVER_ERROR:
					setError(createVerificationError(VerificationErrorType.SERVER_ERROR));
					break;
			}

			setIsLoading(false);
		};

		performAuthorization();
	}, [token]);

	if (isLoading) {
		return (
			<div className={styles.container}>
				<div className={styles.iconContainer}>
					<div className={styles.iconCircle}>
						<Spinner />
					</div>
				</div>
				<div className={styles.loadingPlaceholder} />
				<div className={styles.descriptionPlaceholder} />
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<div className={styles.iconContainer}>
				{isSuccess ? (
					<div className={clsx(styles.iconCircle, styles.iconCircleSuccess)}>
						<CheckIcon className={styles.icon} weight="bold" />
					</div>
				) : (
					<div className={clsx(styles.iconCircle, styles.iconCircleError)}>
						<XIcon className={styles.icon} weight="bold" />
					</div>
				)}
			</div>

			{isSuccess ? (
				<>
					<h1 className={styles.title}>
						<Trans>IP address authorized</Trans>
					</h1>
					<p className={styles.description}>
						<Trans>Your IP address has been successfully authorized.</Trans>
					</p>
				</>
			) : (
				<>
					<h1 className={styles.title}>
						<Trans>Authorization failed</Trans>
					</h1>
					<p className={styles.description}>{renderErrorMessage(error)}</p>
					<div className={styles.footer}>
						<div>
							<AuthRouterLink to="/login" className={styles.link}>
								<Trans>Go to login</Trans>
							</AuthRouterLink>
						</div>
					</div>
				</>
			)}
		</div>
	);
});

export default AuthorizeIPPage;
